<template>
  <Teleport to="body">
    <Transition
      name="action-search-fade"
      appear>
      <div
        v-if="isOpen"
        class="fixed inset-0 top-[var(--env-bar-height)] z-2000 flex items-start justify-center pt-[10vh]"
        @keydown.escape.prevent.stop="close">
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/40"
          @click="close" />

        <!-- Dialog -->
        <div class="relative w-[680px] max-w-[90vw] rounded-xl bg-surface shadow-2xl ring-1 ring-black/10">
          <!-- Search input -->
          <div class="flex items-center gap-3 border-b border-border-subtle px-4 py-3.5">
            <IconLucideSearch class="h-4 w-4 shrink-0 text-text-secondary" />
            <input
              ref="searchInput"
              v-model="query"
              type="text"
              autocomplete="off"
              class="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-secondary"
              :placeholder="$t('W: Søg eller udfør handling...')"
              @keydown.up.exact.prevent="onArrowUp"
              @keydown.down.exact.prevent="onArrowDown"
              @keydown.up.meta.prevent="onSectionBackward"
              @keydown.down.meta.prevent="onSectionForward"
              @keydown.up.ctrl.prevent="onSectionBackward"
              @keydown.down.ctrl.prevent="onSectionForward"
              @keydown.enter.prevent="onEnter" />
          </div>
          <!-- Close button -->
          <div
            class="absolute top-0 right-0 h-12 w-12 cursor-pointer p-3 text-text-primary"
            @click="close">
            <IconLucideX />
          </div>

          <!-- Content -->
          <div
            ref="scrollContainer"
            class="max-h-[420px] overflow-y-auto py-2">
            <!-- No query: Actions, grouped by entity type, then recently visited pages -->
            <template v-if="!query.trim()">
              <template
                v-for="group in groupedActions"
                :key="group.type">
                <div class="px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                  {{ group.label }}
                </div>
                <button
                  v-for="action in group.items"
                  :key="action.key"
                  type="button"
                  :data-index="action.index"
                  :class="[
                    'flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors',
                    activeIndex === action.index ? 'bg-surface-muted' : '',
                  ]"
                  @mouseenter="activeIndex = action.index"
                  @click="executeAction(action)">
                  <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-text-secondary">
                    <template v-if="action.key === 'create-order'">
                      <IconLucidePackagePlus class="h-3.5 w-3.5" />
                    </template>
                    <template v-else-if="action.key === 'create-business-customer'">
                      <IconLucideBuilding2 class="h-3.5 w-3.5" />
                    </template>
                    <template v-else-if="action.key === 'create-private-customer'">
                      <IconLucideUserRoundPlus class="h-3.5 w-3.5" />
                    </template>
                    <template v-else-if="action.key === 'invite-employee'">
                      <IconLucideMail class="h-3.5 w-3.5" />
                    </template>
                    <template v-else-if="action.key === 'create-employee'">
                      <IconLucideUserPlus class="h-3.5 w-3.5" />
                    </template>
                    <template v-else-if="action.key === 'receive-orders'">
                      <IconLucidePackageCheck class="h-3.5 w-3.5" />
                    </template>
                    <template v-else-if="action.key === 'open-profile'">
                      <IconLucideUser class="h-3.5 w-3.5" />
                    </template>
                    <template v-else-if="action.key === 'update-pin'">
                      <IconLucideKeyRound class="h-3.5 w-3.5" />
                    </template>
                  </span>
                  <span class="text-sm font-medium text-text-primary">{{ action.label }}</span>
                </button>
              </template>

              <template v-if="recentPageItems.length > 0">
                <div class="px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                  {{ $t('W: Senest besøgt') }}
                </div>
                <button
                  v-for="(item, index) in recentPageItems"
                  :key="item.id"
                  type="button"
                  :data-index="actions.length + index"
                  :class="[
                    'flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors',
                    activeIndex === actions.length + index ? 'bg-surface-muted' : '',
                  ]"
                  @mouseenter="activeIndex = actions.length + index"
                  @click="navigateToRecentPage(item)">
                  <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-text-secondary">
                    <IconLucideClock class="h-3.5 w-3.5" />
                  </span>
                  <span class="flex items-center gap-1.5">
                    <DevBadge v-if="item.isDev" />
                    <span class="text-sm font-medium text-text-primary">{{ item.label }}</span>
                  </span>
                </button>
              </template>
            </template>

            <!-- Has query: Search results -->
            <template v-else>
              <template v-if="groupedResults.length > 0">
                <template
                  v-for="group in groupedResults"
                  :key="group.type">
                  <div class="px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                    {{ group.label }}
                  </div>
                  <button
                    v-for="result in group.items"
                    :key="result.key"
                    type="button"
                    :data-index="result.flatIndex"
                    :class="[
                      'flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors',
                      activeIndex === result.flatIndex ? 'bg-surface-muted' : '',
                    ]"
                    @mouseenter="activeIndex = result.flatIndex"
                    @click="navigateToResult(result)">
                    <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-text-secondary">
                      <template v-if="result.type === 'page'">
                        <IconLucideFileText class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.type === 'order'">
                        <IconLucidePackage class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.type === 'sample'">
                        <IconLucideTestTube class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.type === 'customer'">
                        <IconLucideBuilding2 class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.type === 'insurance-customer'">
                        <IconLucideHandshake class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.type === 'employee'">
                        <IconLucideUser class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.type === 'customer-user-business'">
                        <IconLucideBuilding2 class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.type === 'customer-user-private'">
                        <IconLucideUserRound class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.key === 'action-create-order'">
                        <IconLucidePackagePlus class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.key === 'action-create-business-customer'">
                        <IconLucideBuilding2 class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.key === 'action-create-private-customer'">
                        <IconLucideUserRoundPlus class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.key === 'action-invite-employee'">
                        <IconLucideMail class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.key === 'action-create-employee'">
                        <IconLucideUserPlus class="h-3.5 w-3.5" />
                      </template>
                      <template v-else-if="result.key === 'action-receive-orders'">
                        <IconLucidePackageCheck class="h-3.5 w-3.5" />
                      </template>
                    </span>
                    <div class="min-w-0 flex-1">
                      <span class="flex items-center gap-1.5">
                        <DevBadge v-if="result.isDev" />
                        <span class="block truncate text-sm font-medium text-text-primary">
                          {{ result.label }}
                        </span>
                      </span>
                      <span
                        v-if="result.sublabel"
                        class="block truncate text-xs text-text-secondary">
                        {{ result.sublabel }}
                      </span>
                    </div>
                  </button>
                </template>
              </template>

              <!-- No results -->
              <div
                v-else
                class="px-4 py-10 text-center text-sm text-text-secondary">
                {{ $t('W: Ingen resultater for') }} "{{ query }}"
              </div>
            </template>
          </div>

          <!-- Footer -->
          <div class="flex items-center gap-4 border-t border-border-subtle px-4 py-2.5 text-xs text-text-secondary">
            <span class="flex items-center gap-1.5">
              <kbd class="rounded border border-border-subtle px-1.5 py-0.5">↑↓</kbd>
              {{ $t('W: naviger') }}
            </span>
            <span class="flex items-center gap-1.5">
              <kbd class="rounded border border-border-subtle px-1.5 py-0.5">{{ metaShortcut('↑↓') }}</kbd>
              {{ $t('W: hop mellem grupper') }}
            </span>
            <span class="flex items-center gap-1.5">
              <kbd class="rounded border border-border-subtle px-1.5 py-0.5">↵</kbd>
              {{ $t('W: åbn') }}
            </span>
            <span class="flex items-center gap-1.5">
              <kbd class="rounded border border-border-subtle px-1.5 py-0.5">Esc</kbd>
              {{ $t('W: luk') }}
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia'
import { useActionSearchStore } from '@/stores/actionSearchStore'
import { useModalStore } from '@/stores/modalStore'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'
import { useOrderStore } from '@/stores/orderStore'
import type { Order } from '@/globals/typescript/models/Order'
import type { IOrderSample } from '@team-milva/lab-models/resources'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useUserStore } from '@/stores/userStore'
import { matchesQuery } from '@/globals/typescript/utils/search'
import { usePageLookupItems, type IPageLookupItem } from '@/composables/usePageLookupItems'
import { getRecentPageIDs, addRecentPageID } from '@/globals/typescript/utils/pageLookupRecents'
import { metaShortcut } from '@/globals/typescript/utils/platform'
import { resolveIsInternal } from '@team-milva/lab-models'
import { formatOrderNumber } from '@/globals/typescript/utils/internalLabel'

// --- Extensible registration interfaces ---

export interface IActionItem {
  key: string
  type: 'order' | 'customer' | 'employee' | 'account'
  label: string
  execute: () => void
}

interface IActionGroup {
  type: string
  label: string
  items: Array<IActionItem & { index: number }>
}

export interface ISearchResult {
  key: string
  type:
    | 'page'
    | 'order'
    | 'sample'
    | 'customer'
    | 'employee'
    | 'customer-user-business'
    | 'customer-user-private'
    | 'insurance-customer'
    | 'action'
  label: string
  sublabel: string
  flatIndex: number
  navigate: () => void
  isDev?: boolean
}

interface IResultGroup {
  type: string
  label: string
  items: ISearchResult[]
}

// Maximum results shown per category
export const MAX_PER_CATEGORY = 5

// Display/keyboard-nav order for the grouped "Handlinger" actions list
const ACTION_GROUP_ORDER: IActionItem['type'][] = ['order', 'customer', 'employee', 'account']

export default {
  name: 'ActionSearchBar',

  setup() {
    return { pageItems: usePageLookupItems() }
  },

  data() {
    return {
      query: '',
      activeIndex: 0,
      recentPageIDs: [] as string[],
    }
  },

  computed: {
    ...mapState(useActionSearchStore, ['isOpen']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useUserStore, ['isLabAdminUser']),
    ...mapState(useAccountStore, ['labAccount', 'allLabAccountUsers']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useCustomerAccountStore, ['allCustomerAccounts']),
    ...mapState(useCustomerUserStore, [
      'customerUsersBusiness',
      'customerUsersPrivate',
    ]),
    ...mapState(useInsuranceCustomerAccountStore, ['allInsuranceCustomerAccounts']),

    metaShortcut(): typeof metaShortcut {
      return metaShortcut
    },

    labSlug(): string {
      return this.labAccount?.subDomain ?? ''
    },

    // Recently visited pages, in most-recent-first order, dropping any page
    // the user no longer has permission for (or that no longer exists).
    // Only navigations made through this bar count as "visited" — see
    // navigateToRecentPage() / pageResults().
    recentPageItems(): IPageLookupItem[] {
      const byID = new Map(this.pageItems.map((item: IPageLookupItem) => [item.id, item]))
      return this.recentPageIDs
        .map((id) => byID.get(id))
        .filter((item): item is IPageLookupItem => item !== undefined)
    },

    // --- Actions registry ---
    // Add new actions here to extend without touching the component template.
    // Each action is gated by the same permission its normal entry point elsewhere
    // in the app requires (see OrdersView.vue / CustomersView.vue / EmployeesListView.vue).
    actions(): IActionItem[] {
      const actions: IActionItem[] = []

      if (this.canUser('orders.create')) {
        actions.push({
          key: 'create-order',
          type: 'order',
          label: this.$t('W: Opret ordre'),
          execute: () => {
            const modal = useModalStore()
            modal.open({
              name: 'CreateOrderModal',
              width: 'md',
              useEasyClose: false,
              props: {
                onSuccess: () => {
                  this.$router.push({
                    name: 'Orders',
                    params: { labSlug: this.labSlug },
                  })
                },
              },
            })
            this.close()
          },
        })
      }

      if (this.canUser('customers.manage')) {
        actions.push({
          key: 'create-business-customer',
          type: 'customer',
          label: this.$t('W: Opret erhvervskunde'),
          execute: () => {
            const modal = useModalStore()
            modal.open({
              name: 'CreateCustomerModal',
              width: 'sm',
              props: {
                defaultCustomerType: 'business',
                onSuccess: (customerAccountID: string) => {
                  this.$router.push({
                    name: 'CustomerEditGeneral',
                    params: { labSlug: this.labSlug, customerAccountID },
                  })
                },
              },
            })
            this.close()
          },
        })

        actions.push({
          key: 'create-private-customer',
          type: 'customer',
          label: this.$t('W: Opret privatkunde'),
          execute: () => {
            const modal = useModalStore()
            modal.open({
              name: 'CreateCustomerModal',
              width: 'sm',
              props: {
                defaultCustomerType: 'private',
                onSuccess: (privateUserID: string) => {
                  this.$router.push({
                    name: 'CustomerPrivateUserEditGeneral',
                    params: { labSlug: this.labSlug, privateUserID },
                  })
                },
              },
            })
            this.close()
          },
        })
      }

      if (this.canUser('orders.registerSamples')) {
        actions.push({
          key: 'receive-orders',
          type: 'order',
          label: this.$t('W: Modtag ordrer'),
          execute: () => {
            const modal = useModalStore()
            modal.open({ name: 'ReceiveOrdersModal', width: 'lg' })
            this.close()
          },
        })
      }

      if (this.canUser('employees.manage')) {
        actions.push({
          key: 'invite-employee',
          type: 'employee',
          label: this.$t('W: Inviter medarbejder'),
          execute: () => {
            const modal = useModalStore()
            modal.open({
              name: 'InviteModal',
              props: {
                onSuccess: () => {
                  this.$router.push({
                    name: 'Employees',
                    params: { labSlug: this.labSlug },
                  })
                },
              },
            })
            this.close()
          },
        })

        actions.push({
          key: 'create-employee',
          type: 'employee',
          label: this.$t('W: Opret medarbejder'),
          execute: () => {
            const modal = useModalStore()
            modal.open({
              name: 'CreateEmployeeModal',
              props: {
                onSuccess: (userID: string) => {
                  this.$router.push({
                    name: 'EmployeeEditDetails',
                    params: { labSlug: this.labSlug, userID },
                  })
                },
              },
            })
            this.close()
          },
        })
      }

      // Your own account — no permission, everyone signed in has one. Mirrors HeaderUserMenu.vue:
      // a lab admin visiting a lab has no lab-management profile (their profile lives in the
      // lab-admin app), so the menu disables "Profil" for them and offers "Opdater PIN-kode"
      // instead. Same here, except the profile entry is left out rather than shown disabled.
      if (this.isLabAdminUser) {
        actions.push({
          key: 'update-pin',
          type: 'account',
          label: this.$t('W: Opdater PIN-kode'),
          execute: () => {
            const modal = useModalStore()
            modal.open({ name: 'UpdatePinCodeModal', width: 'sm' })
            this.close()
          },
        })
      } else {
        actions.push({
          key: 'open-profile',
          type: 'account',
          label: this.$t('W: Min profil'),
          execute: () => {
            const modal = useModalStore()
            modal.open({ name: 'UserProfileModal', width: 'md' })
            this.close()
          },
        })
      }

      return actions.sort((a, b) => {
        const groupDiff = ACTION_GROUP_ORDER.indexOf(a.type) - ACTION_GROUP_ORDER.indexOf(b.type)
        if (groupDiff !== 0) return groupDiff
        return a.label.localeCompare(b.label)
      })
    },

    // Groups the already-sorted `actions` list for display, mirroring the
    // `groupedResults` pattern below. `index` on each item is its position
    // in the flat `actions` array — this is what keyboard nav (activeIndex)
    // and executeAction() key off, so it must stay in sync with `actions`.
    groupedActions(): IActionGroup[] {
      const groups: IActionGroup[] = []
      const byType = new Map<string, Array<IActionItem & { index: number }>>()

      this.actions.forEach((action, index) => {
        const existing = byType.get(action.type) ?? []
        existing.push({ ...action, index })
        byType.set(action.type, existing)
      })

      const groupDefs: Array<{ type: IActionItem['type']; label: string }> = [
        { type: 'order', label: this.$t('W: Ordrer') },
        { type: 'customer', label: this.$t('W: Kunder') },
        { type: 'employee', label: this.$t('W: Medarbejdere') },
        { type: 'account', label: this.$t('W: Min konto') },
      ]

      for (const def of groupDefs) {
        const items = byType.get(def.type)
        if (items && items.length > 0) {
          groups.push({ type: def.type, label: def.label, items })
        }
      }

      return groups
    },

    // --- Search results ---
    // Add new search categories here. Each category produces ISearchResult[] entries
    // with a `flatIndex` assigned after all categories are merged.

    pageResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      return this.pageItems
        .filter((item: IPageLookupItem) => matchesQuery(item.label, q))
        .slice(0, MAX_PER_CATEGORY)
        .map((item: IPageLookupItem) => ({
          key: `page-${item.id}`,
          type: 'page' as const,
          label: item.label,
          sublabel: item.section ?? '',
          isDev: item.isDev,
          navigate: () => {
            this.$router.push({ name: item.routeName })
            addRecentPageID(this.labSlug, item.id, Date.now())
            this.close()
          },
        }))
    },

    orderResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()
      // The order number is displayed "#"-prefixed (see `label` below), but
      // stored without it — strip a leading "#" so the query matches either way.
      const orderNumberQuery = q.replace(/^#/, '')

      return this.allOrders
        .filter((order) => {
          // labPrefix is intentionally excluded — it's the shared root every
          // sample code is generated from (`${labPrefix}-${n}`), so matching
          // on it here would resurface the order for any partial sample-label
          // query, which is exactly what the separate Samples category avoids.
          if (matchesQuery(order.orderNumber, orderNumberQuery)) return true
          if (matchesQuery(order.caseNumber, q)) return true
          return false
        })
        .slice(0, MAX_PER_CATEGORY)
        .map((order) => ({
          key: `order-${order.id}`,
          type: 'order' as const,
          label: order.orderNumber
            ? formatOrderNumber(
                order.orderNumber,
                resolveIsInternal(order.options)
              )
            : order.labPrefix,
          sublabel: order.caseNumber
            ? this.$t('W: Sagsnr.') + ` ${order.caseNumber}`
            : '',
          navigate: () => {
            this.$router.push({
              name: 'Order',
              params: { labSlug: this.labSlug, orderID: order.id },
            })
            this.close()
          },
        }))
    },

    // Sample labels are their own result category — a match here never
    // surfaces the parent order in `orderResults` (see Decisions Made).
    sampleResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      const matches: { order: Order; sample: IOrderSample }[] = []
      for (const order of this.allOrders) {
        for (const sample of order.samples) {
          if (sample.label.id && matchesQuery(sample.label.id, q)) {
            matches.push({ order, sample })
          }
        }
      }

      return matches
        .slice(0, MAX_PER_CATEGORY)
        .map(({ order, sample }) => ({
          key: `sample-${sample.sampleID}`,
          type: 'sample' as const,
          label: sample.label.id,
          sublabel: order.orderNumber
            ? this.$t('W: Ordre') + ` #${order.orderNumber}`
            : '',
          navigate: () => {
            this.$router.push({
              name: 'OrderSample',
              params: {
                labSlug: this.labSlug,
                orderID: order.id,
                sampleID: sample.sampleID,
              },
            })
            this.close()
          },
        }))
    },

    customerResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      return this.allCustomerAccounts
        .filter((customer) => matchesQuery(customer.name, q))
        .slice(0, MAX_PER_CATEGORY)
        .map((customer) => ({
          key: `customer-${customer.id}`,
          type: 'customer' as const,
          label: customer.name,
          sublabel: '',
          navigate: () => {
            this.$router.push({
              name: 'CustomerEditGeneral',
              params: { labSlug: this.labSlug, customerAccountID: customer.id },
            })
            this.close()
          },
        }))
    },

    employeeResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      return this.allLabAccountUsers
        .filter((user) => {
          if (matchesQuery(user.fullName, q)) return true
          if (matchesQuery(user.email, q)) return true
          if (matchesQuery(user.formattedPhoneNumber, q)) return true
          return false
        })
        .slice(0, MAX_PER_CATEGORY)
        .map((user) => ({
          key: `employee-${user.id}`,
          type: 'employee' as const,
          label: user.fullName,
          sublabel: user.email,
          navigate: () => {
            this.$router.push({
              name: 'EmployeeEditDetails',
              params: { labSlug: this.labSlug, userID: user.id },
            })
            this.close()
          },
        }))
    },

    businessAccountUserResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      return this.customerUsersBusiness
        .filter((user) => {
          if (matchesQuery(user.fullName, q)) return true
          if (matchesQuery(user.email, q)) return true
          if (matchesQuery(user.formattedPhoneNumber, q)) return true
          return false
        })
        .slice(0, MAX_PER_CATEGORY)
        .map((user) => ({
          key: `customer-user-business-${user.id}`,
          type: 'customer-user-business' as const,
          label: user.fullName,
          sublabel: user.email,
          navigate: () => {
            this.$router.push({
              name: 'CustomerUserEditDetails',
              params: {
                labSlug: this.labSlug,
                customerAccountID: user.customerAccountID,
                customerUserID: user.id,
              },
            })
            this.close()
          },
        }))
    },

    privateUserResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      return this.customerUsersPrivate
        .filter((user) => {
          if (matchesQuery(user.fullName, q)) return true
          if (matchesQuery(user.email, q)) return true
          if (matchesQuery(user.formattedPhoneNumber, q)) return true
          return false
        })
        .slice(0, MAX_PER_CATEGORY)
        .map((user) => ({
          key: `customer-user-private-${user.id}`,
          type: 'customer-user-private' as const,
          label: user.fullName,
          sublabel: user.email,
          navigate: () => {
            this.$router.push({
              name: 'CustomerPrivateUserEditGeneral',
              params: { labSlug: this.labSlug, privateUserID: user.id },
            })
            this.close()
          },
        }))
    },

    insuranceCustomerAccountResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      return this.allInsuranceCustomerAccounts
        .filter((insuranceCustomerAccount) => matchesQuery(insuranceCustomerAccount.name, q))
        .slice(0, MAX_PER_CATEGORY)
        .map((insuranceCustomerAccount) => ({
          key: `insurance-customer-${insuranceCustomerAccount.id}`,
          type: 'insurance-customer' as const,
          label: insuranceCustomerAccount.name,
          sublabel: '',
          navigate: () => {
            this.$router.push({
              name: 'InsuranceCustomerAccountEditGeneral',
              params: { labSlug: this.labSlug, insuranceCustomerAccountID: insuranceCustomerAccount.id },
            })
            this.close()
          },
        }))
    },

    // Actions (the same "Handlinger" registry used in the empty-query view)
    // are searchable by label too, but always sorted last — see groupDefs
    // order in groupedResults below.
    actionResults(): Omit<ISearchResult, 'flatIndex'>[] {
      if (!this.query.trim()) return []
      const q = this.query.trim()

      return this.actions
        .filter((action) => matchesQuery(action.label, q))
        .slice(0, MAX_PER_CATEGORY)
        .map((action) => ({
          key: `action-${action.key}`,
          type: 'action' as const,
          label: action.label,
          sublabel: '',
          navigate: () => {
            action.execute()
          },
        }))
    },

    // Flat list of all results with assigned flatIndex for keyboard navigation.
    // Concatenation order here must match `groupDefs` order in `groupedResults`
    // below — flatIndex numbering has to line up with visual top-to-bottom
    // order for arrow-key navigation (and section-jump) to make sense.
    flatResults(): ISearchResult[] {
      const all: Omit<ISearchResult, 'flatIndex'>[] = [
        ...this.pageResults,
        ...this.orderResults,
        ...this.sampleResults,
        ...this.customerResults,
        ...this.insuranceCustomerAccountResults,
        ...this.employeeResults,
        ...this.businessAccountUserResults,
        ...this.privateUserResults,
        ...this.actionResults,
      ]
      return all.map((result, index) => ({ ...result, flatIndex: index }))
    },

    // Grouped results for display
    groupedResults(): IResultGroup[] {
      const groups: IResultGroup[] = []
      const byType = new Map<string, ISearchResult[]>()

      for (const result of this.flatResults) {
        const existing = byType.get(result.type) ?? []
        existing.push(result)
        byType.set(result.type, existing)
      }

      const groupDefs: Array<{ type: string; label: string }> = [
        { type: 'page', label: this.$t('W: Sider') },
        { type: 'order', label: this.$t('W: Ordrer') },
        { type: 'sample', label: this.$t('W: Prøver') },
        { type: 'customer', label: this.$t('W: Kunder') },
        { type: 'insurance-customer', label: this.$t('W: Forsikringskunder') },
        { type: 'employee', label: this.$t('W: Medarbejdere') },
        { type: 'customer-user-business', label: this.$t('W: Erhvervsbrugere') },
        { type: 'customer-user-private', label: this.$t('W: Privatkunder') },
        { type: 'action', label: this.$t('W: Handlinger') },
      ]

      for (const def of groupDefs) {
        const items = byType.get(def.type)
        if (items && items.length > 0) {
          groups.push({ type: def.type, label: def.label, items })
        }
      }

      return groups
    },

    totalResultCount(): number {
      return this.flatResults.length
    },

    totalItemCount(): number {
      return this.query.trim()
        ? this.totalResultCount
        : this.actions.length + this.recentPageItems.length
    },

    // Starting flatIndex/index of each visible group, in display order — used
    // by onSectionForward()/onSectionBackward() to jump between groups. Picks
    // the empty-view groups (actions + "Senest besøgt") or the search-result
    // groups depending on whether there's a query, mirroring totalItemCount.
    sectionStarts(): number[] {
      if (this.query.trim()) {
        return this.groupedResults
          .map((group) => group.items[0]?.flatIndex)
          .filter((start): start is number => start !== undefined)
      }
      const starts = this.groupedActions
        .map((group) => group.items[0]?.index)
        .filter((start): start is number => start !== undefined)
      if (this.recentPageItems.length > 0) {
        starts.push(this.actions.length)
      }
      return starts
    },
  },

  watch: {
    isOpen(opened: boolean) {
      if (opened) {
        this.query = ''
        this.activeIndex = 0
        this.recentPageIDs = getRecentPageIDs(this.labSlug)
        this.$nextTick(() => {
          ;(this.$refs.searchInput as HTMLInputElement)?.focus()
        })
      }
    },

    query() {
      this.activeIndex = 0
      this.scrollActiveIntoView()
    },

    activeIndex() {
      this.scrollActiveIntoView()
    },
  },

  methods: {
    ...mapActions(useActionSearchStore, ['close']),
    ...mapActions(useUserStore, ['canUser']),

    // Keyboard nav (arrows, section-jump) moves activeIndex without any
    // native scrolling — the active row can end up outside the visible
    // viewport. Rows carry a `data-index` attribute matching activeIndex so
    // it can be looked up and scrolled into view here.
    //
    // When the active row is the first item of a section, its header is
    // included in what must stay visible — otherwise the header can end up
    // scrolled just out of view while the item itself already counts as
    // "visible enough" on its own, so no scroll happens and the user loses
    // track of which section they've landed in. This can't be done with a
    // plain `element.scrollIntoView()` call (it only knows about one
    // element), so the visible range is computed manually instead.
    scrollActiveIntoView() {
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer as HTMLElement | undefined
        const activeItem = container?.querySelector(
          `[data-index="${this.activeIndex}"]`
        ) as HTMLElement | undefined
        if (!container || !activeItem) return

        const isFirstInGroup = this.sectionStarts.includes(this.activeIndex)
        const header = isFirstInGroup
          ? (activeItem.previousElementSibling as HTMLElement | null)
          : null
        const topTarget = header ?? activeItem

        const containerRect = container.getBoundingClientRect()
        const topOffset =
          topTarget.getBoundingClientRect().top - containerRect.top + container.scrollTop
        const bottomOffset =
          activeItem.getBoundingClientRect().bottom - containerRect.top + container.scrollTop

        if (topOffset < container.scrollTop) {
          container.scrollTop = topOffset
        } else if (bottomOffset > container.scrollTop + container.clientHeight) {
          container.scrollTop = bottomOffset - container.clientHeight
        }
      })
    },

    executeAction(action: IActionItem) {
      action.execute()
    },

    navigateToResult(result: ISearchResult) {
      result.navigate()
    },

    navigateToRecentPage(item: IPageLookupItem) {
      this.$router.push({ name: item.routeName })
      addRecentPageID(this.labSlug, item.id, Date.now())
      this.close()
    },

    onEnter() {
      if (!this.query.trim()) {
        if (this.activeIndex < this.actions.length) {
          const action = this.actions[this.activeIndex]
          if (action) this.executeAction(action)
        } else {
          const item = this.recentPageItems[this.activeIndex - this.actions.length]
          if (item) this.navigateToRecentPage(item)
        }
      } else {
        const result = this.flatResults[this.activeIndex]
        if (result) this.navigateToResult(result)
      }
    },

    onArrowDown() {
      if (this.totalItemCount === 0) return
      this.activeIndex = (this.activeIndex + 1) % this.totalItemCount
    },

    onArrowUp() {
      if (this.totalItemCount === 0) return
      this.activeIndex =
        (this.activeIndex - 1 + this.totalItemCount) % this.totalItemCount
    },

    // Cmd/Ctrl+ArrowDown/Up — jump to the first item of the next/previous
    // section (always the section's first item, in both directions), rather
    // than moving one item at a time. Wraps at both ends, like plain arrows.
    onSectionForward() {
      const starts = this.sectionStarts
      if (starts.length === 0) return
      const next = starts.find((start) => start > this.activeIndex)
      this.activeIndex = next ?? starts[0] ?? 0
    },

    onSectionBackward() {
      const starts = this.sectionStarts
      if (starts.length === 0) return
      const previous = [...starts].reverse().find((start) => start < this.activeIndex)
      this.activeIndex = previous ?? starts[starts.length - 1] ?? 0
    },
  },
}
</script>

<style scoped>
.action-search-fade-enter-active,
.action-search-fade-leave-active {
  transition: opacity 120ms ease;
}
.action-search-fade-enter-from,
.action-search-fade-leave-to {
  opacity: 0;
}
</style>
